import type { ICustomerRef } from '@team-milva/lab-models/customer'

/**
 * Insurance customers ordering for themselves (MIL-402, Phase 6).
 *
 * An insurance customer's own orders and projects are ALWAYS insurance cases with the insurance
 * customer itself as the insurer — lab-cloud forces it and rejects anything else. So for an
 * insurance user the app shows no "Forsikringssag" toggle and no insurer picker, only the
 * (optional) insurance number, and every payload carries `isInsuranceCase: true` plus its own ID.
 * Business users keep the toggle + picker exactly as before.
 */

/** The signed-in user's own insurance customer ID, or `null` for a business or private user. */
export function getOwnInsuranceCustomerAccountID(
  user: { customer?: ICustomerRef | null } | null | undefined
): string | null {
  const customer = user?.customer
  if (!customer || customer.type !== 'insurance' || !customer.id) return null
  return customer.id
}

/**
 * The insurance customers a business user may name as insurer on an order or project: the active
 * ones linked to the user's own business customer (`customer.type === 'business'`). Empty for an
 * insurance or private user — they get no insurer picker.
 */
export function getActiveInsuranceCustomerAccountsForUser<
  T extends { activeState: string; customerAccountIDs: string[] },
>(user: { customer?: ICustomerRef | null } | null | undefined, insuranceCustomerAccounts: T[]): T[] {
  const customer = user?.customer
  if (!customer || customer.type !== 'business' || !customer.id) return []
  const customerAccountID = customer.id
  return insuranceCustomerAccounts.filter(
    (insuranceCustomerAccount) =>
      insuranceCustomerAccount.activeState === 'active' &&
      insuranceCustomerAccount.customerAccountIDs.includes(customerAccountID)
  )
}

/** What the order/project forms hold for the insurance case, before it goes to the API. */
export interface IInsuranceCaseFormValues {
  isInsuranceCase: boolean
  insurancePartnerID: string | null | undefined
  insuranceNumber: string | null | undefined
}

/** The insurance-case fields of a create/update order or project request body. */
export interface IInsuranceCaseRequestFields {
  isInsuranceCase: boolean
  insurancePartnerID?: string
  insuranceNumber?: string
}

/**
 * The request fields for the form's insurance-case values. For an insurance user
 * (`ownInsuranceCustomerAccountID` set) the case is always on and the insurer is always the user's
 * own insurance customer, whatever the form holds; only the insurance number is taken from it.
 * Otherwise the business toggle decides, and an off toggle sends neither insurer nor number.
 *
 * An update passes the record's saved number as `previousInsuranceNumber`: emptying a saved number
 * then sends `''`, which lab-cloud stores, instead of omitting the field — an omitted field is
 * "unchanged", so the clear would never reach the API. An empty field with nothing saved (and every
 * create) still omits it.
 */
export function resolveInsuranceCaseFields(
  ownInsuranceCustomerAccountID: string | null,
  values: IInsuranceCaseFormValues,
  { previousInsuranceNumber }: { previousInsuranceNumber?: string | null } = {}
): IInsuranceCaseRequestFields {
  const insuranceNumber = values.insuranceNumber || (previousInsuranceNumber ? '' : undefined)

  if (ownInsuranceCustomerAccountID) {
    return {
      isInsuranceCase: true,
      insurancePartnerID: ownInsuranceCustomerAccountID,
      insuranceNumber,
    }
  }

  return {
    isInsuranceCase: values.isInsuranceCase,
    insurancePartnerID: values.isInsuranceCase ? values.insurancePartnerID || undefined : undefined,
    insuranceNumber: values.isInsuranceCase ? insuranceNumber : undefined,
  }
}

/**
 * Whether order prices are shown to this user — the one rule every order price surface reads.
 *
 * - Insurance user: shown, unless their insurance customer has "Skjul priser i kunde-app" on.
 *   Their orders are always insurance cases, priced from their own `InsuranceCustomerAccountPrices`
 *   — what they see is what they are billed.
 * - Business user: shown, unless the order is an insurance case (the lab bills the insurer, so the
 *   customer's own price would be misleading) or the account has prices hidden.
 * - Anyone else (a private customer has no price list): never.
 *
 * `isInsuranceCase` is the order's own flag; leave it out for a surface that is not about one
 * order (e.g. the account's price list).
 */
export function isPriceVisibleToUser(input: {
  user: { customer?: ICustomerRef | null } | null | undefined
  isPricesHiddenInCustomerApp: boolean
  isInsuranceCase?: boolean
}): boolean {
  const customerType = input.user?.customer?.type
  if (input.isPricesHiddenInCustomerApp) return false
  if (customerType === 'insurance') return true
  if (customerType === 'business') return !input.isInsuranceCase
  return false
}

/**
 * The insurer to name on an order or project row, or `null` for none. An insurance user's own
 * orders all name the user's own insurance customer as insurer — repeating "who you are" on every
 * row adds nothing, so only another insurer (a business customer's insurance case) is named.
 */
export function getDisplayedInsurerID(
  record: { insurancePartnerID?: string | null },
  ownInsuranceCustomerAccountID: string | null
): string | null {
  if (!record.insurancePartnerID) return null
  if (record.insurancePartnerID === ownInsuranceCustomerAccountID) return null
  return record.insurancePartnerID
}
